import React, { createContext, useContext } from "react";

/* ------------------------------------------------------------------
   Small presentation helpers shared by the SBCF pages.

   <Segmented>  — iOS-style segmented control for switching views/filters
                  (mirrors the MUI ToggleButtonGroup look from
                  theme/MuiThemeBridge.js for plain buttons).
   <Notice>     — tinted callout for warnings, errors and confirmations.
   SbcfEmbedContext — set by the /sbcf and /sbcfadmin dashboards so a view
                  they render (e.g. BlueprintSpecSheet, which also has its
                  own route at /sbcf/rfq) knows the page shell + header are
                  already drawn and it should render as a section instead.
------------------------------------------------------------------ */

export const SbcfEmbedContext = createContext(false);
export const useSbcfEmbedded = () => useContext(SbcfEmbedContext);

/**
 *   <Segmented
 *     ariaLabel="View"
 *     value={view}
 *     onChange={setView}
 *     options={[{ value: "a", label: "A" }, { value: "b", label: "B", disabled: true, title: "Coming soon" }]}
 *   />
 */
export const Segmented = ({ options, value, onChange, ariaLabel, className = "" }) => (
  <div className={`max-w-full overflow-x-auto ${className}`}>
    <div
      role="group"
      aria-label={ariaLabel}
      className="inline-flex gap-0.5 rounded-[0.8rem] bg-[color:var(--app-hover)] p-[3px]"
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            disabled={option.disabled}
            title={option.title}
            aria-pressed={active}
            className={`inline-flex min-h-[2.25rem] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[0.6rem] px-3.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              active
                ? "bg-white text-[color:var(--app-text)] shadow-[0_1px_3px_rgba(15,23,42,0.12)] dark:bg-white/[0.14] dark:shadow-none"
                : "app-muted hover:text-[color:var(--app-text)]"
            }`}
          >
            {option.icon}
            {option.label}
          </button>
        );
      })}
    </div>
  </div>
);

// Literal strings so Tailwind keeps them.
const NOTICE_TONES = {
  amber: "border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-200",
  rose: "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300",
  emerald: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  sky: "border-sky-500/30 bg-sky-500/10 text-sky-800 dark:text-sky-200",
};

export const Notice = ({ tone = "amber", className = "", children, ...rest }) => (
  <div
    className={`rounded-xl border px-4 py-3 text-sm ${NOTICE_TONES[tone] || NOTICE_TONES.amber} ${className}`}
    {...rest}
  >
    {children}
  </div>
);
